import { FormControlWrapper } from "@cmp-src/form/FormControlWrapper";
import {
  useFormControlInputA11y,
  useFormControlWrapper
} from "@cmp-src/form/FormControlWrapper/FormControlWrapperProvider";
import { splitFormControlWrapperProps } from "@cmp-src/form/FormControlWrapper/helpers";
import { Popover } from "@cmp-src/overlay/Popover/Popover";
import { Button } from "@cmp-src/primitives/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from "@cmp-src/primitives/command";
import { cn, useDisclosure } from "@gettactic/helpers";
import { Check, PlusCircle } from "lucide-react";
import React from "react";
import {
  FilterableSelectProps,
  FilterableSelectPropsWithFormControl
} from "./types";

/**
 * The bare combobox of `FilterableSelect`, without label or error: a trigger button that opens
 * a searchable list. A custom `trigger` must be a single button element.
 */
export const FilterableSelectInput = React.forwardRef<
  HTMLInputElement,
  FilterableSelectProps
>((props, ref) => {
  const {
    trigger,
    onSelect,
    value,
    items,
    placeholder = "Select",
    searchPlaceholder = "Search...",
    triggerProps,
    allowCustomValue = false,
    validateCustomValue,
    getCustomValueSuggestions,
    disabled = false,
    popoverContentClassName
  } = props;
  const comboboxDisclosure = useDisclosure();
  const [searchValue, setSearchValue] = React.useState("");
  // Inside FilterableSelect the field label points at the trigger by id
  const { id } = useFormControlWrapper();
  const a11yProps = useFormControlInputA11y();

  // Get valid custom value suggestions based on search input
  const customValueSuggestions = React.useMemo(() => {
    if (!allowCustomValue || !searchValue) return [];

    // Get suggestions from the prop or use search value directly
    const suggestions = getCustomValueSuggestions
      ? getCustomValueSuggestions(searchValue)
      : [searchValue];

    // Filter out suggestions that already exist in items and validate them
    return suggestions.filter((suggestion) => {
      const existsInItems = items.some(
        (item) => item.value.toLowerCase() === suggestion.toLowerCase()
      );
      if (existsInItems) return false;
      if (validateCustomValue) {
        return validateCustomValue(suggestion);
      }
      return true;
    });
  }, [
    allowCustomValue,
    searchValue,
    items,
    validateCustomValue,
    getCustomValueSuggestions
  ]);

  const hasCustomValueSuggestions = customValueSuggestions.length > 0;

  const handleSelectCustomValue = (valueToSelect: string) => {
    if (valueToSelect) {
      onSelect?.(valueToSelect);
      comboboxDisclosure.close();
      setSearchValue("");
    }
  };

  const handleOpenChange = (open: boolean) => {
    comboboxDisclosure.onOpenChange(open);
    if (!open) {
      setSearchValue("");
    }
  };

  // Get display value - check items first, then use raw value for custom values
  const displayValue = React.useMemo(() => {
    if (!value) return null;
    const foundItem = items.find((item) => item.value === value);
    return foundItem?.label ?? value;
  }, [value, items]);

  return (
    <Popover
      className={"p-0"}
      bodyProps={{ className: "p-0" }}
      contentProps={{
        className: cn(popoverContentClassName),
        "aria-label": placeholder
      }}
      triggerAsChild
      content={
        <Command
          // Values are often ids: match the label too (passed as keywords)
          filter={(value, searchTerm, keywords = []) => {
            const term = searchTerm.toLowerCase();
            return [value, ...keywords].some((text) =>
              text?.toLowerCase().includes(term)
            )
              ? 1
              : 0;
          }}
          value={value}
          className={"p-0"}
        >
          <CommandInput
            placeholder={searchPlaceholder}
            inputMask={props.mask}
            wrapperClassName={"border-0 shadow-none px-4"}
            inputClassName={"h-5 py-3 outline-none"}
            className={"relative border-b-1 border-b-neutral-100 p-0.5"}
            value={searchValue}
            onValueChange={setSearchValue}
            onKeyDown={(e) => {
              if (e.key === "Enter" && customValueSuggestions.length === 1) {
                e.preventDefault();
                handleSelectCustomValue(customValueSuggestions[0]);
              }
            }}
          />
          <CommandList>
            {/* Show custom value options when valid - forceMount to bypass filter */}
            {hasCustomValueSuggestions && (
              <CommandGroup forceMount>
                {customValueSuggestions.map((suggestion) => (
                  <CommandItem
                    key={`custom-${suggestion}`}
                    value={`custom-${suggestion}`}
                    onSelect={() => handleSelectCustomValue(suggestion)}
                    className="text-primary"
                    forceMount
                  >
                    <PlusCircle className="mr-2 h-4 w-4" />
                    Use "{suggestion}"
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {!hasCustomValueSuggestions && (
              <CommandEmpty>
                {allowCustomValue && searchValue
                  ? "Type a valid time format (e.g., 09:30 AM)"
                  : "No items found."}
              </CommandEmpty>
            )}
            <CommandGroup>
              {items.map((item) => (
                <CommandItem
                  key={item.value}
                  value={item.value}
                  keywords={
                    typeof item.label === "string" ? [item.label] : undefined
                  }
                  onSelect={(currentValue) => {
                    onSelect?.(currentValue);
                    comboboxDisclosure.close();
                    setSearchValue("");
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === item.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {item.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      }
      open={comboboxDisclosure.isOpen}
      onOpenChange={handleOpenChange}
    >
      {trigger || (
        <Button
          variant="trigger"
          className={cn(
            "w-full",
            disabled &&
              "opacity-60 cursor-not-allowed bg-neutral-100 text-neutral-400 hover:bg-neutral-100"
          )}
          size={"trigger"}
          // biome-ignore lint/a11y/useSemanticElements: <explanation>
          role="combobox"
          aria-expanded={comboboxDisclosure.isOpen}
          disabled={disabled}
          id={id}
          {...a11yProps}
          {...triggerProps}
        >
          {displayValue ?? placeholder}
        </Button>
      )}
    </Popover>
  );
});

/**
 * A labelled combobox with a search field, for picking one of many `{ value, label }` items.
 * The search matches values and string labels. With `allowCustomValue` the typed text can
 * become the value (see `TimeSelect`). For multiple choice or nested items use
 * `UnifiedSelectInput`.
 */
export const FilterableSelect = React.forwardRef<
  HTMLInputElement,
  FilterableSelectPropsWithFormControl
>((props, ref) => {
  const [wrapperProps, inputProps] = splitFormControlWrapperProps(props);

  return (
    <>
      <FormControlWrapper variant={"unstyled"} {...wrapperProps}>
        <FilterableSelectInput ref={ref} {...inputProps} />
      </FormControlWrapper>
    </>
  );
});

FilterableSelect.displayName = "FilterableSelect";
